'use client';
import formatPhoneNumber from '@/helpers/formatPhoneNumber';
import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useMemo, useRef } from 'react';
import styles from './map.module.scss';
export interface BaseCoordinate {
  latitude: number;
  longitude: number;
  title: string | null;
  address: string | undefined;
  workDays: string;
  phone: string | null;
}

export interface CoordinateWithId extends BaseCoordinate {
  id: string;
}

interface MapComponentProps {
  coordinates: (BaseCoordinate | CoordinateWithId)[];
  height?: string | number;
  width?: string | number;
  zoom?: number;
  focusedZoom?: number;
  reg?: boolean;
  selectedId?: string | null;
  center?: [number, number] | null;
  onPointSelect: (id: string) => void;
}

const RegMapComponent: FC<MapComponentProps> = ({
  coordinates,
  height = '540px',
  width = '100%',
  zoom = 12,
  focusedZoom = 15,
  center,
  reg = false,
  selectedId,
  onPointSelect,
}) => {
  const mapRef = useRef<ymaps.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const prevSelectedIdRef = useRef<string | null | undefined>();
  const t = useTranslations('Components.regmap');

  const defaultMapState = useMemo(() => {
    if (center) {
      return { center: center, zoom: zoom };
    }

    const validCoords = coordinates.filter(c => c?.latitude && c?.longitude);
    if (validCoords.length === 0) {
      return { center: [41.2995, 69.2401], zoom: zoom };
    }
    if (validCoords.length === 1) {
      return {
        center: [validCoords[0].latitude, validCoords[0].longitude],
        zoom: zoom,
      };
    }
    const sumLat = validCoords.reduce((sum, coord) => sum + coord.latitude, 0);
    const sumLong = validCoords.reduce(
      (sum, coord) => sum + coord.longitude,
      0
    );
    const avgCenter: [number, number] = [
      sumLat / validCoords.length,
      sumLong / validCoords.length,
    ];
    return { center: avgCenter, zoom: zoom };
  }, [coordinates, center, zoom]);

  useEffect(() => {
    if (
      mapRef.current &&
      reg &&
      selectedId &&
      selectedId !== prevSelectedIdRef.current
    ) {
      const selectedCoord = coordinates.find(
        (coord): coord is CoordinateWithId =>
          typeof coord === 'object' &&
          coord !== null &&
          'id' in coord &&
          coord.id === selectedId
      );

      if (selectedCoord?.latitude && selectedCoord?.longitude) {
        const targetCoords: [number, number] = [
          selectedCoord.latitude,
          selectedCoord.longitude,
        ];

        mapRef.current
          .panTo(targetCoords, {
            flying: false,
            duration: 800,
          })
          .then(() => {
            // @ts-ignore
            mapRef.current.setZoom(focusedZoom, {
              duration: 300,
              checkZoomRange: true,
            });
          })
          .catch((err: any) => {
            console.error('Error during map panTo/setZoom:', err);
          });
      }
    }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, reg, coordinates, focusedZoom]);

  useEffect(() => {
    const handleMapClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const selectButton = target.closest('.select-pickup-point-btn');
      const closeButton = target.closest('.custom-balloon-close-btn');

      if (selectButton instanceof HTMLElement && selectButton.dataset.id) {
        const pointId = selectButton.dataset.id;
        onPointSelect(pointId);
        if (mapRef.current) {
          mapRef.current.balloon.close();
        }
      } else if (closeButton instanceof HTMLElement) {
        if (mapRef.current) {
          mapRef.current.balloon.close();
        }
      }
    };

    const currentMapContainer = mapContainerRef.current;
    if (currentMapContainer) {
      currentMapContainer.addEventListener('click', handleMapClick);
    }

    return () => {
      if (currentMapContainer) {
        currentMapContainer.removeEventListener('click', handleMapClick);
      }
    };
  }, [onPointSelect]);

  return (
    <div className={styles.mapWrapper} ref={mapContainerRef}>
      <YMaps
        query={{
          lang: 'en_RU',
          load: 'package.full',
        }}
      >
        <Map
          defaultState={defaultMapState}
          width={width}
          height={height}
          modules={[
            'control.ZoomControl',
            'control.FullscreenControl',
            'geoObject.addon.balloon',
            'geoObject.addon.hint',
          ]}
          instanceRef={instance => {
            mapRef.current = instance;
          }}
        >
          {coordinates.map((coord, index) => {
            const hasId = 'id' in coord && typeof coord.id === 'string';
            return (
              <Placemark
                key={hasId ? (coord as CoordinateWithId).id : index}
                geometry={[coord.latitude, coord.longitude]}
                options={{
                  iconLayout: 'default#image',
                  iconImageHref: '/placemark-icon.svg',
                  iconImageSize: [30, 32],
                  iconImageOffset: [-15, -32],
                  balloonCloseButton: false,
                  hideIconOnBalloonOpen: false,
                  balloonOffset: [0, -35],
                }}
                properties={{
                  hintContent: coord.title || `Location ${index + 1}`,
                  balloonContent: `
                    <div class="custom-balloon-content">
                        <div class="header">
                            <h3>${coord.title || `Location ${index + 1}`}</h3>
                            <button type="button" class="custom-balloon-close-btn">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="vuesax/linear/close-circle">
                                    <g id="close-circle">
                                    <path id="Vector" d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#D1D5DB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path id="Vector_2" d="M9.16992 14.8299L14.8299 9.16992" stroke="#D1D5DB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path id="Vector_3" d="M14.8299 14.8299L9.16992 9.16992" stroke="#D1D5DB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </g>
                                    </g>
                                </svg>
                            </button>
                        </div>
                        <div class="texts">
                            ${coord.address ? `<p>${coord.address}</p>` : ''}
                            ${coord.phone ? `<p>${formatPhoneNumber(coord.phone ?? '')}</p>` : ''}
                            ${coord.workDays ? `<p>${coord.workDays}</p>` : ''}
                        </div>
                        <button type="button" class="btn select-pickup-point-btn"  data-id="${
                          (coord as CoordinateWithId).id
                        }"">${t('select')}</button>
                    </div>
                `,
                }}
              />
            );
          })}
        </Map>
      </YMaps>
    </div>
  );
};

export default RegMapComponent;
